import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, ShieldX, Loader2, Search } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20";

export default function VerifyLicense() {
  const [licenseId, setLicenseId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const check = async (e) => {
    e.preventDefault();
    if (!licenseId.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/licenses/verify/${encodeURIComponent(licenseId.trim())}`);
      setResult(await res.json());
    } catch (err) {
      setResult({ found: false, message: "Verification is unavailable right now. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <Link to="/" data-testid="verify-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-navy">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to VeraChart
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-4 py-16 sm:px-6 lg:py-24" data-testid="verify-page">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">License Verification</p>
        <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy">Verify a VeraChart Team License</h1>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          Enter a VeraChart license ID (for example, VC-ACT-XXXXXXXX) to confirm a team's license status. Only
          limited, non-sensitive information is shown.
        </p>

        <form onSubmit={check} className="mt-8 flex gap-3">
          <input
            value={licenseId}
            onChange={(e) => setLicenseId(e.target.value)}
            placeholder="VC-ACT-XXXXXXXX"
            className={`${inputCls} font-mono uppercase`}
            data-testid="verify-license-input"
            aria-label="License ID"
            maxLength={40}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="verify-license-button"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-teal-600 px-6 py-3 font-display text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Search className="h-4 w-4" aria-hidden="true" />}
            Verify
          </button>
        </form>

        {result && (
          <div
            className={`mt-8 rounded-2xl border p-7 ${result.found && result.valid ? "border-teal-200 bg-teal-50" : "border-slate-200 bg-white"}`}
            data-testid="verify-result"
          >
            <div className="flex items-center gap-3">
              {result.found && result.valid ? (
                <ShieldCheck className="h-7 w-7 text-teal-600" aria-hidden="true" />
              ) : (
                <ShieldX className="h-7 w-7 text-slate-400" aria-hidden="true" />
              )}
              <p className="font-display text-lg font-bold text-navy" data-testid="verify-result-message">{result.message}</p>
            </div>
            {result.found && (
              <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 border-t border-slate-200/70 pt-5 sm:grid-cols-2">
                <div><dt className="text-xs text-slate-500">Organization</dt><dd className="text-sm font-semibold text-navy" data-testid="verify-result-org">{result.organization}</dd></div>
                <div><dt className="text-xs text-slate-500">Team</dt><dd className="text-sm font-semibold text-navy">{result.team}</dd></div>
                <div><dt className="text-xs text-slate-500">Team Type</dt><dd className="text-sm font-semibold text-navy">{result.team_type}</dd></div>
                <div><dt className="text-xs text-slate-500">Status</dt><dd className="text-sm font-semibold text-navy" data-testid="verify-result-status">{result.status}</dd></div>
              </dl>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white py-8">
        <p className="mx-auto max-w-xl px-4 text-xs text-slate-400 sm:px-6">© 2026 VeraChart. All rights reserved.</p>
      </footer>
    </div>
  );
}
